import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import { ascend, descend, prop, sort, sortWith, toLower } from 'ramda';

import { filterByString } from '@topconagriculture/frontend-utils';

import { Ingredient, IngredientDailyUsage } from './ingredients.types';
import { RootState } from '../../reducers';
import { Forms } from '../../form/form.selector';
import { IngredientsOrderTypes } from './ingredients.constants';

type IngredientList = Ingredient[];

export const getAllIngredients = (state: RootState): IngredientList => state.entities.ingredients.ingredientList;

export const getIngredients = createSelector(
  [getAllIngredients],
  (ingredients): IngredientList => ingredients.filter((ingredient) => !ingredient.deleted),
);

export const getDailyUsage = (state: RootState): IngredientDailyUsage => state.entities.ingredients.dailyUsage;

export const getIngredientsWithoutPremixes = createSelector([getIngredients], (ingredientList) =>
  ingredientList.filter((ing) => !ing.premix),
);

export const getIngredientsWithoutPremixesAndRefusals = createSelector(
  [getIngredientsWithoutPremixes],
  (ingredientList) => ingredientList.filter((ingredient) => !ingredient.refusal),
);

export const getIngredientExists = createSelector(
  getIngredients,
  (ingredientList): boolean => ingredientList.length > 0,
);

export const checkIngredientIsPremix = createSelector(
  getIngredients,
  (ingredients: IngredientList) => (ingredientId: string) => {
    return ingredients.find((ingredient) => ingredient.id === ingredientId)?.premix;
  },
);

export const getNumberOfIngredientsWithoutPremixes = createSelector(
  [getIngredientsWithoutPremixes],
  (ingList: IngredientList): number => ingList.length,
);

const orderByTypeFn: any = (orderType: IngredientsOrderTypes = IngredientsOrderTypes.alph) => {
  const orderMap = {
    [IngredientsOrderTypes.alph]: sort((elem1: Ingredient, elem2: Ingredient) =>
      toLower(elem1.description).localeCompare(toLower(elem2.description)),
    ),
    [IngredientsOrderTypes.dryasc]: sortWith([ascend(prop('dryMatterPerc'))]),
    [IngredientsOrderTypes.drydesc]: sortWith([descend(prop('dryMatterPerc'))]),
    [IngredientsOrderTypes.priceasc]: sortWith([ascend(prop('priceDry'))]),
    [IngredientsOrderTypes.pricedesc]: sortWith([descend(prop('priceDry'))]),
  };
  return orderMap[orderType];
};

const ingredientsFilterForm = formValueSelector(Forms.ingredientFilterForm);
const ingredientNameSelector = (state: RootState) => ingredientsFilterForm(state, 'ingredientName');
const ingredientTypeSelector = (state: RootState) => ingredientsFilterForm(state, 'ingredientType');
const ingredientOrderSelector = (state: RootState) => ingredientsFilterForm(state, 'ingredientOrder');
const ingredientInUseSelector = (state: RootState) => ingredientsFilterForm(state, 'hideNotInUse');

export const getIngredientListFiltered = createSelector(
  [
    getIngredientsWithoutPremixes,
    ingredientNameSelector,
    ingredientTypeSelector,
    ingredientOrderSelector,
    ingredientInUseSelector,
  ],
  (
    ingredientList: Ingredient[],
    ingredientName: string,
    ingredientType: number,
    ingredientOrder: IngredientsOrderTypes,
    hideNotInUse: boolean,
  ): IngredientList => {
    const filterString = ingredientName ?? '';
    const filteredList: IngredientList = ingredientList.filter((ingredient, index, array) => {
      return (
        array.findIndex((t) => t.id === ingredient.id) === index &&
        (ingredientType !== -1 ? ingredientType === ingredient.ingredientTypeCode : ingredient) &&
        (hideNotInUse ? !ingredient.notInUse : true) &&
        (filterByString(ingredient.displayName, filterString) || filterByString(ingredient.description, filterString))
      );
    });

    return orderByTypeFn(ingredientOrder)(filteredList);
  },
);

export const getOrderIngredientListFiltered = createSelector([getIngredientListFiltered], (ingredientList) => {
  return ingredientList.filter((ingredient) => ingredient.orderControl);
});

export const getIngredientById = createSelector(
  [getAllIngredients],
  (ingredientList: Ingredient[]) =>
    (ingredientId: string): Ingredient | undefined => {
      return ingredientList.find((ingredient) => ingredient.id === ingredientId);
    },
);

export const getIngredientByIdNormal = createSelector(
  [getAllIngredients, (state: RootState, ingredientId: string) => ingredientId],
  (ingredientList: Ingredient[], ingredientId: string) => {
    return ingredientList.find((ingredient) => ingredient.id === ingredientId);
  },
);

export const getWaterId = createSelector([getIngredients], (ingredientsList: IngredientList) => {
  const waterIng: Ingredient | undefined = ingredientsList.find((ing: Ingredient) => ing.dryMatterPerc === 0);
  return waterIng ? waterIng.id : undefined;
});

export const getWaterIds = createSelector([getAllIngredients], (ingredientsList: IngredientList) =>
  ingredientsList.filter((ing: Ingredient) => ing.dryMatterPerc === 0).map((ing) => ing.id),
);

export const getRefusalIngredients = createSelector(getIngredients, (ingredients) =>
  ingredients.filter((x) => x.refusal),
);

export const isIngredientRefusal = (state: RootState, id: string) => {
  const refusalIngredients = getRefusalIngredients(state);
  return refusalIngredients.map((x) => x.id).includes(id);
};
